/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { AdapterConfig, UserAuth } from '@repo/common';
import { getPrivateClient } from '@repo/sharedlib/src/data/common.data';
import { isErrorWithMessage } from '@repo/sharedlib/src/utils/error.util';
import { parse } from '@repo/toolslib/src/utils/serialize.util';
import { isAxiosError } from 'axios';
import { getTableNamesInSyncOrder } from '../adapter/adapterUtils';

export interface AdapterResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  syncedCount?: number;
  foreignKeyErrors?: Array<{ itemId: string; error: any }>;
  errors?: Array<{ itemId: string; error: any }>;
  status?: number;
}

export interface AdapterSyncResult {
  success: boolean;
  syncedCount: number;
  errors: string[];
}

// ─── ADAPTER DATA FUNCTIONS ───

/**
 * Get all items from a specific table on the server
 * @param tableName - The table name to fetch items from
 * @param config - Adapter configuration
 * @returns Array of items in common format
 */
export async function getAdapterItems<T = any>(
  tableName: string,
  authToken?: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  config?: Partial<AdapterConfig>,
  minimal: boolean = false
): Promise<T[]> {
  try {
    // Use existing client infrastructure
    if (!authToken) {
      throw new Error('Auth token is required for client-side operations');
    }
    const client = getPrivateClient(authToken, config);
    const response = await client.get(`/adapter/${tableName}${minimal ? '?minimal=true' : ''}`);

    const items = response.data?.items || [];
    // Parse response data to handle dates and other serialized values
    return parse(JSON.stringify(items));
  } catch (error) {
    console.error(`Error getting items from ${tableName}:`, error);
    throw new Error(isErrorWithMessage(error) ? error.message : 'Unknown error');
  }
}

/**
 * Get a specific item by ID from a table on the server
 * @param tableName - The table name to fetch from
 * @param id - The item ID to fetch
 * @param config - Adapter configuration
 * @returns The item or null if not found
 */
export async function getAdapterItemById<T = any>(
  tableName: string,
  id: string,
  authToken?: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  config?: Partial<AdapterConfig>,
  minimal: boolean = false
): Promise<T | null> {
  try {
    // Use existing client infrastructure
    if (!authToken) {
      throw new Error('Auth token is required for client-side operations');
    }
    const client = getPrivateClient(authToken, config);
    const response = await client.get(
      `/adapter/${tableName}/${id}${minimal ? '?minimal=true' : ''}`
    );

    const item = response.data?.item || null;
    // Parse response data to handle dates and other serialized values
    return parse(JSON.stringify(item));
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      // If the item is not found, return null
      return null;
    }
    console.error(`Error getting item ${id} from ${tableName}:`, error);
    throw new Error(isErrorWithMessage(error) ? error.message : 'Unknown error');
  }
}

/**
 * Get multiple items by IDs from a table on the server
 * @param tableName - The table name to fetch from
 * @param ids - The item IDs to fetch
 * @param config - Adapter configuration
 * @returns The items found (missing IDs are omitted)
 */
export async function getAdapterItemsByIds<T = any>(
  tableName: string,
  ids: string[],
  authToken?: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  config?: Partial<AdapterConfig>,
  minimal: boolean = false,
  extra?: { includeTranscript?: boolean }
): Promise<T[]> {
  try {
    if (!authToken) {
      throw new Error('Auth token is required for client-side operations');
    }
    const client = getPrivateClient(authToken, config);
    const resp = await client.post(
      `/adapter/${tableName}/batch`,
      {
        ids,
        minimal,
        includeTranscript: extra?.includeTranscript
      },
      { timeout: (config as any)?.timeout }
    );
    const items = resp.data?.items || [];
    return parse(JSON.stringify(items));
  } catch (error) {
    console.error(`Error getting items by IDs from ${tableName}:`, error);
    throw new Error(isErrorWithMessage(error) ? error.message : 'Unknown error');
  }
}

/**
 * Send items to a specific table on the server
 * @param tableName - The table name to send items to
 * @param items - Array of items in common format
 * @param config - Adapter configuration
 * @returns Success response with sync count
 */
export async function sendAdapterItems<T extends { id: string }>(
  tableName: string,
  items: T[],
  authToken?: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  config?: Partial<AdapterConfig>
): Promise<AdapterResponse> {
  try {
    // Use existing client infrastructure
    if (!authToken) {
      throw new Error('Auth token is required for client-side operations');
    }
    const client = getPrivateClient(authToken, config);
    const response = await client.post(`/adapter/${tableName}`, { items });

    // Handle 400 errors that were converted to 200 responses by the interceptor
    if (response.data?.status === 400) {
      console.warn(`HTTP 400 error for ${tableName} batch operation: ${response.data.error}`);
      return {
        success: false,
        syncedCount: 0,
        error: response.data.error || 'Bad Request',
        status: 400
      };
    }

    // Check if the response indicates foreign key constraint errors
    if (response.status === 400 && response.data?.code === 'FOREIGN_KEY_CONSTRAINT') {
      console.warn(
        `Foreign key constraint errors for ${tableName}: ` +
          `${response.data.failedCount} items failed, ${response.data.syncedCount} succeeded. ` +
          `Failed items will be retried in future syncs when dependencies are available.`
      );

      // Return a success response for foreign key constraint errors
      // since these are expected and will be resolved in future syncs
      return {
        success: true,
        syncedCount: response.data.syncedCount || 0,
        error: 'Foreign key constraint violations (will retry in future syncs)',
        foreignKeyErrors: response.data.errors
      };
    }

    // Check if the response indicates unique constraint errors
    if (response.status === 400 && response.data?.code === 'UNIQUE_CONSTRAINT') {
      console.warn(
        `Unique constraint errors for ${tableName}: ` +
          `${response.data.failedCount} items failed, ${response.data.syncedCount} succeeded. ` +
          `These items already exist in the database and will be skipped.`
      );

      // Return a success response for unique constraint errors
      // since these are expected and items already exist
      return {
        success: true,
        syncedCount: response.data.syncedCount || 0,
        error: 'Unique constraint violations (items already exist)',
        errors: response.data.errors
      };
    }

    return response.data || { success: false, error: 'No response data' };
  } catch (error: any) {
    // Check if this is a 400 error with foreign key constraint details
    if (error.response?.status === 400 && error.response?.data?.code === 'FOREIGN_KEY_CONSTRAINT') {
      console.warn(
        `Foreign key constraint errors for ${tableName}: ` +
          `${error.response.data.failedCount} items failed, ${error.response.data.syncedCount} succeeded. ` +
          `Failed items will be retried in future syncs when dependencies are available.`
      );

      // Return a success response for foreign key constraint errors
      // since these are expected and will be resolved in future syncs
      return {
        success: true,
        syncedCount: error.response.data.syncedCount || 0,
        error: 'Foreign key constraint violations (will retry in future syncs)',
        foreignKeyErrors: error.response.data.errors
      };
    }

    // Check if this is a 400 error with unique constraint details
    if (error.response?.status === 400 && error.response?.data?.code === 'UNIQUE_CONSTRAINT') {
      console.warn(
        `Unique constraint errors for ${tableName}: ` +
          `${error.response.data.failedCount} items failed, ${error.response.data.syncedCount} succeeded. ` +
          `These items already exist in the database and will be skipped.`
      );

      // Return a success response for unique constraint errors
      // since these are expected and items already exist
      return {
        success: true,
        syncedCount: error.response.data.syncedCount || 0,
        error: 'Unique constraint violations (items already exist)',
        errors: error.response.data.errors
      };
    }

    console.debug(`Error sending items to ${tableName}:`, error?.message);
    console.log(error);

    throw new Error(isErrorWithMessage(error) ? error.message : 'Unknown error');
  }
}

/**
 * Send a single item to a specific table on the server
 * @param tableName - The table name to send item to
 * @param item - The item in common format
 * @param authToken - Authentication token
 * @param config - Adapter configuration
 * @returns Success response
 */
export async function sendAdapterItem<T extends { id: string }>(
  tableName: string,
  item: T,
  authToken?: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  config?: Partial<AdapterConfig>
): Promise<AdapterResponse> {
  if (!authToken) {
    throw new Error('Auth token is required for client-side operations');
  }
  const client = getPrivateClient(authToken, config);
  try {
    if (typeof item.id !== 'string' || item.id.length > 100) {
      console.error(`Item ID is required, got:`, item.id);
      throw new Error(`Item ID is required`);
    }
    const response = await client.put(`/adapter/${tableName}/${item.id}`, { item });

    // Handle 400 errors that were converted to 200 responses by the interceptor
    if (response.data?.status === 400) {
      console.warn(
        `HTTP 400 error for ${tableName} item ${(item as any).id}: ${response.data.error}`
      );
      return {
        success: false,
        syncedCount: 0,
        error: response.data.error || 'Bad Request',
        status: 400
      };
    }

    // Check if the response indicates foreign key constraint errors
    if (response.status === 400 && response.data?.code === 'FOREIGN_KEY_CONSTRAINT') {
      console.warn(
        `Foreign key constraint error for ${tableName} item ${response.data.itemId}: ` +
          `${response.data.details?.message || response.data.error}. ` +
          `Failed items will be retried in future syncs when dependencies are available.`
      );

      // Return a success response for foreign key constraint errors
      // since these are expected and will be resolved in future syncs
      return {
        success: true,
        syncedCount: 0, // Single item failed
        error: 'Foreign key constraint violations (will retry in future syncs)',
        foreignKeyErrors: [
          {
            itemId: response.data.itemId,
            error: response.data.details
          }
        ]
      };
    }

    return response.data || { success: false, error: 'No response data' };
  } catch (error) {
    console.log(`Error sending item to ${tableName}:`, error);
    throw new Error(isErrorWithMessage(error) ? error.message : 'Unknown error');
  }
}

/**
 * Sync items from client to server with retry logic
 * @param tableName - The table name to sync
 * @param items - Array of items to sync
 * @param config - Adapter configuration
 * @returns Sync result with success status and error details
 */
export async function syncAdapterItems<T extends { id: string }>(
  tableName: string,
  items: T[],
  authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  retries: number = 3
): Promise<AdapterSyncResult> {
  let attempts = 0;
  const errors: string[] = [];

  while (attempts < retries) {
    try {
      const response = await sendAdapterItems(tableName, items, authToken);

      if (response.success) {
        return {
          success: true,
          syncedCount: response.syncedCount || items.length,
          errors: []
        };
      } else {
        errors.push(response.error || 'Unknown sync error');
      }
    } catch (error) {
      const errorMessage = isErrorWithMessage(error) ? error.message : 'Unknown error';
      errors.push(errorMessage);
    }

    attempts++;

    // Wait before retrying (exponential backoff)
    if (attempts < retries) {
      await delay(Math.pow(2, attempts) * 1000);
    }
  }

  return {
    success: false,
    syncedCount: 0,
    errors
  };
}

/**
 * Sync items from server to client with retry logic
 * @param tableName - The table name to sync
 * @param config - Adapter configuration
 * @returns Array of items from server
 */
export async function syncAdapterItemsFromServer<T = any>(
  tableName: string,
  authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  retries: number = 3
): Promise<T[]> {
  let attempts = 0;

  while (attempts < retries) {
    try {
      return await getAdapterItems<T>(tableName, authToken);
    } catch (error) {
      attempts++;

      // Wait before retrying (exponential backoff)
      if (attempts < retries) {
        await delay(Math.pow(2, attempts) * 1000);
      } else {
        throw error;
      }
    }
  }

  throw new Error('Failed to sync items from server after all retries');
}

// ─── PRIVATE HELPER FUNCTIONS ───

/**
 * Delay execution for a specified number of milliseconds
 * @param ms - Milliseconds to delay
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─── BATCH OPERATIONS ───

/**
 * Sync multiple tables in parallel
 * @param tables - Array of table names to sync
 * @param authToken - Authentication token
 * @returns Results for each table
 */
export async function syncMultipleTables<T = any>(
  tables: string[],
  authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<Record<string, T[]>> {
  const results: Record<string, T[]> = {};

  await Promise.all(
    tables.map(async (tableName) => {
      try {
        const items = await syncAdapterItemsFromServer<T>(tableName, authToken);
        results[tableName] = items;
      } catch (error) {
        console.debug(`Error syncing table ${tableName}:`, error);
        results[tableName] = [];
      }
    })
  );

  return results;
}

/**
 * Send items to multiple tables in parallel
 * @param tableItems - Record of table names to items arrays
 * @param authToken - Authentication token
 * @returns Results for each table
 */
export async function sendToMultipleTables<T extends { id: string }>(
  tableItems: Record<string, T[]>,
  authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<Record<string, AdapterSyncResult>> {
  const results: Record<string, AdapterSyncResult> = {};

  const tableNames = getTableNamesInSyncOrder();

  await Promise.all(
    tableNames.map(async (tableName) => {
      const items = tableItems[tableName];
      if (!items) {
        return;
      }
      try {
        const result = await syncAdapterItems(tableName, items, authToken);
        results[tableName] = result;
      } catch (error) {
        console.error(`Error sending to table ${tableName}:`, error);
        results[tableName] = {
          success: false,
          syncedCount: 0,
          errors: [isErrorWithMessage(error) ? error.message : 'Unknown error']
        };
      }
    })
  );

  return results;
}

// ─── UTILITY FUNCTIONS ───

/**
 * Create adapter configuration with defaults
 * @param baseUrl - Base URL of the server
 * @param authToken - Authentication token
 * @param options - Optional configuration overrides
 * @returns Adapter configuration
 */
export function createAdapterConfig(
  baseUrl: string,
  authToken: string,
  options: Partial<AdapterConfig> = {}
): AdapterConfig {
  return {
    baseUrl,
    authToken,
    timeout: 30000,
    retries: 3,
    ...options
  };
}

/**
 * Validate adapter configuration
 * @param config - Adapter configuration to validate
 * @returns True if valid, throws error if invalid
 */
export function validateAdapterConfig(config: AdapterConfig): boolean {
  if (!config.baseUrl) {
    throw new Error('Missing baseUrl in adapter config');
  }
  if (!config.authToken) {
    throw new Error('Missing authToken in adapter config');
  }
  if (config.timeout && config.timeout < 1000) {
    throw new Error('Timeout must be at least 1000ms');
  }
  if (config.retries && (config.retries < 0 || config.retries > 10)) {
    throw new Error('Retries must be between 0 and 10');
  }
  return true;
}
